<template>

  <div ref="captions_mounting_point">

    <v-menu
      top
      right
      offset-y
      :attach   = "$refs.captions_mounting_point"
      min-width = "250"
    >
      <template v-slot:activator="{ on: onMenu, attrs: menuAttrs }">

        <v-tooltip 
          content-class = "tooltip_container"
          bottom
          open-delay    = "500"
          color         = "#151515"
        >

          <template v-slot:activator="{ on: onTooltip, attrs: tooltipAttrs}">
            <v-btn
              icon
              v-bind    = "{...menuAttrs, ...tooltipAttrs}"
              v-on      = "{...onMenu, ...onTooltip}"
              :disabled = "disabled"
              :color    = "areCaptionsShown ? 'primary' : 'default'" 
            >
              <v-icon>mdi-subtitles-outline</v-icon>
            </v-btn>
          </template>
          <span>Select Caption Track</span>
        </v-tooltip>

      </template>

      <v-list
        dense
        :disabled = "disabled"
      >

        <v-list-item-group
          :value  = "selectedSubtitles"
          @change = "onSetSubtitleTrack"
          mandatory
        >

          <!--

          This item can be disabled because it can accidentally trigger. 

          When the user opens this menu and then opens another clip with captions,
          it may take a few beats for captions to be populated. In that time this list
          will attempt to select the "Off" value since it is the only one left and a
          selection in this list is mandatory. However, this will also override the
          persisted selection that has been made for the caption track.

          -->
          <v-list-item
            color     = "primary"
            value     = "Off"
            :disabled = "!allowCaptionsToBeTurnedOff"
          >
            <v-list-item-content>
              <v-list-item-title>Off</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item
            v-for  = "source in subtitleSources"
            color  = "primary"
            :key   = "source.id"
            :value = "source.id"
          >
            <v-list-item-content>
              <v-list-item-title>

                <span v-if="source.label.length < truncatedCaptionLabelLength">{{ source.label }}</span>

                <v-tooltip 
                  v-else
                  right
                  nudge-right="85px"
                  open-delay="500"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind = "attrs"
                          v-on   = "on"
                    >
                      {{ source.label.substring(0,25) }}...
                    </span>
                  </template>
                  <span>{{ source.label }}</span>
                </v-tooltip>

              </v-list-item-title>
            </v-list-item-content>
            <v-list-item-icon
              v-if                = "source.canRemove"
              @click.stop.prevent = "onRemoveSubtitleSource(source.id)"
            >
              <v-icon color="white">mdi-close-circle</v-icon>
            </v-list-item-icon>
          </v-list-item>

        </v-list-item-group>

      </v-list>

    </v-menu>

    <!-- Confirmation Dialog -->
    <v-dialog
      v-model   = "isConfirmationDialogOpen"
      max-width = "290"
    >
      <v-card>
        <v-card-title>Remove {{ sourceToRemove ? sourceToRemove.label : '' }}?</v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn
            text
            @click = "isConfirmationDialogOpen = false"
          >
            Cancel
          </v-btn>

          <v-btn
            color  = "error"
            text
            @click = "isConfirmationDialogOpen = false; onRemovalConfirmed();"
          >
            Remove
          </v-btn>

        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>

</template>

<script>

// #region Imports

/* vuex */
import { mapState } from 'vuex';

/* Telestream */
import {
  REMOVE_SUBTITLE_SOURCE,
  SELECT_SUBTITLE_SOURCE,
  TURN_OFF_SUBTITLES
} from '@/utils/action-types';

// #endregion Imports

export default {

  // #region Properties

  name:  'CaptionsButton',
  props: {
    disabled: Boolean
  },
  data() {
    return {
      isConfirmationDialogOpen:    false,
      sourceToRemove:              undefined,
      truncatedCaptionLabelLength: 25
    }
  },
  computed: {
    ...mapState([
      'videoController',
      'video'
    ]),
    selectedSubtitles() {
      if (!this.videoController) {
        return undefined;
      }

      return this.videoController.currentSubtitles;
    },
    subtitleSources() {
      const sources = [];
      if (!this.videoController) {
        return sources;
      }

      // In-media subtitle sources:
      this.videoController.hlsSubtitles.forEach(s => {
        if (this.videoController.availableServices.includes(s)) {
          sources.push({
            id:        s,
            label:     s,
            canRemove: false
          });
        }
      });

      // User-defined subtitle sources:
      this.videoController.sidecarSubtitles.forEach(s => {
        sources.push({
          id:        s.id,
          label:     s.label.slice(5),  // Remove "user-"
          canRemove: true
        })
      });

      return sources;
    },
    areCaptionsShown() {
      if (!this.videoController) {
        return false;
      }

      return (this.videoController.currentSubtitles !== 'Off');
    },
    allowCaptionsToBeTurnedOff() {
      return this.subtitleSources.length > 0;
    }
  },

  // #endregion Properties

  // #region Events

  methods: {

    onSetSubtitleTrack(sourceId) {
      if (sourceId === 'Off') {
        this.$store.dispatch(TURN_OFF_SUBTITLES);
      } else {
        this.$store.dispatch(SELECT_SUBTITLE_SOURCE, sourceId);
      }
    },

    onRemoveSubtitleSource(sourceId) {
      this.sourceToRemove = this.subtitleSources.find(s => s.id === sourceId);
      if (!this.sourceToRemove) {
        return;
      }

      this.isConfirmationDialogOpen = true;
    },

    onRemovalConfirmed() {
      this.$store.dispatch(REMOVE_SUBTITLE_SOURCE, this.sourceToRemove.id);
      this.sourceToRemove = undefined;
      this.$emit('resize');
    }

  }

  // #endregion Events

}

</script>
